import {
  PriceBookStatus,
  PricebookItem,
  PricebookItemActivePeriod,
  PricebookItemInput,
  PricebookItemPreviewRequest,
} from "../../../../types/price-book.type";
import { ProductListItem } from "../../../../types/product.type";
import {
  PricebookPrice,
  computePricebookPrice,
} from "../../../../utils/pricebookPrice";

/** The API's bounds for a unit price (DECIMAL(12,2)) and for any discount %. */
const MAX_UNIT_PRICE = 9_999_999_999.99;
const MAX_DISCOUNT_PERCENT = 100;
const MONEY_PATTERN = /^\d+(\.\d{1,2})?$/;

/** One product line of the pricebook, as the สินค้า tab edits it. */
export interface PricebookLine {
  productCode: string;
  productUuid: string | null;
  productName: string;
  sequence: number | null;
  unit: string | null;
  /**
   * Draft text of the VAT-inclusive unit price. A line the migration script has not reached
   * starts from its ราคาหน้าซอง, which is what the next save writes for it.
   */
  unitPrice: string;
  /** Draft text of the line's own discount %; "" follows the pricebook's. */
  ownDiscountPercent: string;
  status: PriceBookStatus;
  facePrice: number | null;
  /** Still billed at its legacy price: the next save writes its unit price for the first time. */
  isUnmigrated: boolean;
  /** What the line bills at now, incl VAT (the legacy price on an unmigrated line). */
  storedPriceInclVat: number | null;
  storedPriceExVat: number | null;
  focOption: PriceBookStatus | null;
  discountOption: PriceBookStatus | null;
  /** The price period the line sells at today, as stored; null when none covers today. */
  activePeriod: PricebookItemActivePeriod | null;
  /** Its periods that have not started yet. */
  upcomingPeriodCount: number;
}

export const toAmountText = (value: number | null): string =>
  value === null ? "" : String(value);

const isMoneyText = (text: string): boolean => MONEY_PATTERN.test(text.trim());

export const isValidUnitPrice = (text: string): boolean =>
  isMoneyText(text) && Number(text) <= MAX_UNIT_PRICE;

export const isValidDiscountPercent = (text: string): boolean =>
  isMoneyText(text) && Number(text) <= MAX_DISCOUNT_PERCENT;

/** A line's own % may be left empty, which follows the pricebook's. */
export const isValidOwnDiscount = (text: string): boolean =>
  text.trim() === "" || isValidDiscountPercent(text);

const toOwnDiscount = (text: string): number | null =>
  text.trim() === "" ? null : Number(text);

/**
 * A synced line can carry no product code; there is nothing to send it under, so it is skipped
 * and the next save (which replaces the whole set) takes it off the book. The editor counts
 * these so the confirmation can say so.
 */
export const toPricebookLine = (item: PricebookItem): PricebookLine | null => {
  if (!item.product_code) return null;
  return {
    productCode: item.product_code,
    productUuid: item.product_uuid,
    productName: item.product_name ?? item.product_code,
    sequence: item.sequence ?? null,
    unit: item.unit,
    unitPrice: toAmountText(item.unit_price ?? item.face_price),
    ownDiscountPercent: toAmountText(item.discount_percent),
    status: item.status ?? "enable",
    facePrice: item.face_price,
    isUnmigrated: item.unit_price === null,
    storedPriceInclVat: item.price_incl_vat ?? item.price,
    storedPriceExVat: item.price_ex_vat,
    focOption: item.foc_option,
    discountOption: item.discount_option,
    activePeriod: item.active_period,
    upcomingPeriodCount: item.upcoming_period_count,
  };
};

/** A product put on the book starts at its ราคาหน้าซอง, following the book's %. */
export const toNewPricebookLine = (product: ProductListItem): PricebookLine => ({
  productCode: product.code,
  productUuid: product.uuid,
  productName: product.name,
  sequence: product.sequence ?? null,
  unit: null,
  unitPrice: toAmountText(product.face_price),
  ownDiscountPercent: "",
  status: "enable",
  facePrice: product.face_price,
  isUnmigrated: false,
  storedPriceInclVat: null,
  storedPriceExVat: null,
  focOption: null,
  discountOption: null,
  activePeriod: null,
  upcomingPeriodCount: 0,
});

/**
 * The price the line would be saved at, or null while its unit price or the % in force is
 * not a valid number yet. Computed exactly as the server computes it.
 */
export const computeLinePrice = (
  line: PricebookLine,
  bookDiscountText: string,
): PricebookPrice | null => {
  if (!isValidUnitPrice(line.unitPrice)) return null;
  if (!isValidOwnDiscount(line.ownDiscountPercent)) return null;
  const ownDiscount = toOwnDiscount(line.ownDiscountPercent);
  if (ownDiscount === null && !isValidDiscountPercent(bookDiscountText)) {
    return null;
  }
  return computePricebookPrice({
    unitPrice: Number(line.unitPrice),
    itemDiscountPercent: ownDiscount,
    pricebookDiscountPercent: Number(bookDiscountText),
  });
};

/** True when the unit price is set and is not the product's ราคาหน้าซอง. */
export const differsFromFacePrice = (line: PricebookLine): boolean =>
  line.facePrice !== null &&
  isValidUnitPrice(line.unitPrice) &&
  Number(line.unitPrice) !== line.facePrice;

/** Compared as numbers, so re-typing "15" as "15.00" is not a change. */
export const isSameNumberText = (left: string, right: string): boolean =>
  left.trim() === right.trim() ||
  (left.trim() !== "" && right.trim() !== "" && Number(left) === Number(right));

const isLineChanged = (
  stored: PricebookLine,
  current: PricebookLine,
): boolean =>
  !isSameNumberText(stored.unitPrice, current.unitPrice) ||
  !isSameNumberText(stored.ownDiscountPercent, current.ownDiscountPercent) ||
  stored.status !== current.status;

/** Lines added, removed or edited since the book was loaded. */
export const countLineChanges = (
  storedLines: PricebookLine[],
  lines: PricebookLine[],
): number => {
  const storedByCode = new Map(
    storedLines.map((line) => [line.productCode, line]),
  );
  const currentCodes = new Set(lines.map((line) => line.productCode));
  const changed = lines.filter((line) => {
    const stored = storedByCode.get(line.productCode);
    return !stored || isLineChanged(stored, line);
  }).length;
  const removed = storedLines.filter(
    (line) => !currentCodes.has(line.productCode),
  ).length;
  return changed + removed;
};

/**
 * Lines the save takes off the book that still sell at a period now or have one ahead:
 * PUT /items deletes every period of a removed line, so the confirmation warns first.
 */
export const countRemovedLinesWithPeriods = (
  storedLines: PricebookLine[],
  lines: PricebookLine[],
): number => {
  const currentCodes = new Set(lines.map((line) => line.productCode));
  return storedLines.filter(
    (line) =>
      !currentCodes.has(line.productCode) &&
      (line.activePeriod !== null || line.upcomingPeriodCount > 0),
  ).length;
};

/** Every line the API would refuse: PUT /items needs a valid unit price on each, and any own % in range. */
export const findInvalidLineCodes = (lines: PricebookLine[]): string[] =>
  lines
    .filter(
      (line) =>
        !isValidUnitPrice(line.unitPrice) ||
        !isValidOwnDiscount(line.ownDiscountPercent),
    )
    .map((line) => line.productCode);

/**
 * The membership payload for PUT /pricebooks/:uuid/items: every line, each with its own
 * status. The payload REPLACES the set, so a line left out is taken off the book. `unit`,
 * `foc_option` and `discount_option` ride along so a price save never rewrites them.
 */
export const buildItemInputs = (lines: PricebookLine[]): PricebookItemInput[] =>
  lines.map((line) => {
    const item: PricebookItemInput = {
      product_code: line.productCode,
      unit_price: Number(line.unitPrice),
      discount_percent: toOwnDiscount(line.ownDiscountPercent),
      status: line.status,
    };
    if (line.unit) item.unit = line.unit;
    if (line.focOption) item.foc_option = line.focOption;
    if (line.discountOption) item.discount_option = line.discountOption;
    return item;
  });

/** Prices every line the save would write; `bookDiscountPercent` previews an unsaved book %. */
export const buildPreviewRequest = (
  lines: PricebookLine[],
  bookDiscountPercent: number | null,
): PricebookItemPreviewRequest => {
  const items = lines.map((line) => ({
    product_code: line.productCode,
    unit_price: Number(line.unitPrice),
    discount_percent: toOwnDiscount(line.ownDiscountPercent),
  }));
  return bookDiscountPercent === null
    ? { items }
    : { discount_percent: bookDiscountPercent, items };
};
